/**
 * Lobby — the three-mode landing page (Practice vs AI / Play Online /
 * Card Browser). Mirrors the structure the competing wondersccg.online
 * client uses, since that's what playtesters will recognise.
 *
 * Deck management is a separate concern surfaced above the play modes,
 * not folded into them — selecting "Practice vs AI" or "Play Online"
 * uses the currently-selected deck.
 */

import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { apiPath } from './api.js';
import { useCards } from './wonders-table/hooks/useCards.js';
import { dominantOrbital, orbitalColor } from './wonders-table/types.js';

type DeckSummary = {
  deck_id: string;
  name: string;
  is_tournament: boolean;
  card_ids: readonly string[];
};

type Props = {
  /** Called when the user picks "Play Online" — caller creates the match. */
  onPlayOnline: (deckId: string) => void;
  /** Called when the user picks "Practice vs AI" — caller spawns the AI match. */
  onPracticeAi: (deckId: string) => void;
  /** Called when the user opens "Create Deck" — caller routes to DeckBuilder. */
  onCreateDeck: () => void;
  /** Called when the user clicks "Card Browser". */
  onCardBrowser: () => void;
  /** Called when the user clicks "Browse all" — routes to DeckBrowser. */
  onBrowseDecks: () => void;
  /** Called when the user clicks "Copy & edit" on a starter — clones server-side then opens the builder. */
  onCloneDeck: (sourceDeckId: string) => void;
};

export function Lobby({
  onPlayOnline,
  onPracticeAi,
  onCreateDeck,
  onCardBrowser,
  onBrowseDecks,
  onCloneDeck,
}: Props): ReactElement {
  const [decks, setDecks] = useState<readonly DeckSummary[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(apiPath('/api/decks?game_id=wonders'), {
          credentials: 'include',
        });
        if (!r.ok) return;
        const j = (await r.json()) as { ok: boolean; decks: DeckSummary[] };
        if (cancelled || !j.ok) return;
        setDecks(j.decks);
        // Default-select the first non-tournament deck if any, else the
        // first tournament starter.
        const own = j.decks.find((d) => !d.is_tournament);
        setSelectedDeckId(own?.deck_id ?? j.decks[0]?.deck_id ?? null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = useCards();
  const userDecks = decks.filter((d) => !d.is_tournament);
  const starterDecks = decks.filter((d) => d.is_tournament);

  const orbitalByDeck = useMemo(() => {
    const map = new Map<string, ReturnType<typeof dominantOrbital>>();
    for (const d of decks) {
      map.set(d.deck_id, dominantOrbital(d.card_ids, cards.cards));
    }
    return map;
  }, [decks, cards.cards]);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="text-center">
        <h1 className="font-display text-4xl text-amber-400">Wonders of the First</h1>
        <p className="mt-1 text-sm text-cosmos-400">Collectible Card Game · open playtest</p>
      </header>

      {/* — Decks — */}
      <section className="rounded-md border border-cosmos-800 bg-cosmos-900/50 p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-medium uppercase tracking-wide text-cosmos-400">Decks</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBrowseDecks}
              className="rounded border border-cosmos-700 px-3 py-1 text-xs text-cosmos-200 hover:bg-cosmos-800"
            >
              Browse all
            </button>
            <button
              type="button"
              onClick={onCreateDeck}
              className="rounded bg-violet-600 px-3 py-1 text-xs font-medium text-white hover:bg-violet-500"
            >
              + Create Deck
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-xs text-cosmos-500 italic">Loading…</p>
        ) : (
          <div className="space-y-3 text-sm">
            {userDecks.length > 0 && (
              <div>
                <h3 className="mb-1 text-[11px] uppercase text-cosmos-500">Your decks</h3>
                <ul className="divide-y divide-cosmos-800">
                  {userDecks.map((d) => (
                    <DeckRow
                      key={d.deck_id}
                      deck={d}
                      selected={selectedDeckId === d.deck_id}
                      orbital={orbitalByDeck.get(d.deck_id) ?? null}
                      onSelect={() => setSelectedDeckId(d.deck_id)}
                      onClone={() => onCloneDeck(d.deck_id)}
                    />
                  ))}
                </ul>
              </div>
            )}
            {starterDecks.length > 0 && (
              <div>
                <h3 className="mb-1 text-[11px] uppercase text-cosmos-500">Starter decks</h3>
                <ul className="divide-y divide-cosmos-800">
                  {starterDecks.slice(0, 8).map((d) => (
                    <DeckRow
                      key={d.deck_id}
                      deck={d}
                      selected={selectedDeckId === d.deck_id}
                      orbital={orbitalByDeck.get(d.deck_id) ?? null}
                      onSelect={() => setSelectedDeckId(d.deck_id)}
                      onClone={() => onCloneDeck(d.deck_id)}
                      showCopyAffordance
                    />
                  ))}
                </ul>
                {starterDecks.length > 8 ? (
                  <p className="mt-1 text-[11px] italic text-cosmos-500">
                    {starterDecks.length - 8} more starter decks —{' '}
                    <button
                      type="button"
                      onClick={onBrowseDecks}
                      className="underline hover:text-cosmos-300"
                    >
                      browse all
                    </button>
                    .
                  </p>
                ) : null}
              </div>
            )}
          </div>
        )}
      </section>

      {/* — Play modes — */}
      <section className="grid gap-3 sm:grid-cols-3">
        <ModeCard
          icon="🤖"
          title="Practice against AI"
          description={
            selectedDeckId
              ? `Single-player vs AI using "${decks.find((d) => d.deck_id === selectedDeckId)?.name ?? '?'}"`
              : 'Pick a deck above first'
          }
          disabled={!selectedDeckId}
          onClick={() => selectedDeckId && onPracticeAi(selectedDeckId)}
        />
        <ModeCard
          icon="🌐"
          title="Play Online"
          description="Host or join a 2-player room"
          disabled={!selectedDeckId}
          onClick={() => selectedDeckId && onPlayOnline(selectedDeckId)}
          primary
        />
        <ModeCard
          icon="🔍"
          title="Card Browser"
          description="Search across all cards"
          onClick={onCardBrowser}
        />
      </section>
    </div>
  );
}

function DeckRow({
  deck,
  selected,
  orbital,
  onSelect,
  onClone,
  showCopyAffordance,
}: {
  deck: DeckSummary;
  selected: boolean;
  orbital: ReturnType<typeof dominantOrbital> | null;
  onSelect: () => void;
  onClone: () => void;
  showCopyAffordance?: boolean;
}): ReactElement {
  return (
    <li
      className={`flex items-center gap-2 px-1 py-1.5 ${selected ? 'rounded bg-amber-900/20' : ''}`}
    >
      <span className={`inline-block h-6 w-1 rounded-sm ${orbitalColor(orbital)}`} aria-hidden />
      <button type="button" onClick={onSelect} className="flex flex-1 items-center gap-2 text-left">
        <span
          className={`inline-block h-3 w-3 rounded-full ${selected ? 'bg-amber-400' : 'bg-cosmos-700'}`}
        />
        <span className="font-medium">{deck.name}</span>
        <span className="font-mono text-xs text-cosmos-500">{deck.card_ids.length} cards</span>
        {deck.is_tournament ? (
          <span className="rounded bg-yellow-900/40 px-1.5 py-0.5 text-[10px] uppercase text-yellow-300">
            Starter
          </span>
        ) : null}
      </button>
      {showCopyAffordance ? (
        <button
          type="button"
          onClick={onClone}
          className="text-[11px] text-cosmos-400 hover:text-cosmos-200"
          title="Copy and edit this starter deck"
        >
          Copy & edit
        </button>
      ) : null}
    </li>
  );
}

function ModeCard({
  icon,
  title,
  description,
  onClick,
  disabled,
  primary,
}: {
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group flex flex-col gap-1 rounded-md border p-4 text-left transition disabled:opacity-40 ${
        primary
          ? 'border-violet-700/60 bg-violet-900/30 hover:bg-violet-900/50'
          : 'border-cosmos-700 bg-cosmos-900/40 hover:bg-cosmos-900/70'
      }`}
    >
      <span className="text-2xl">{icon}</span>
      <span className="text-sm font-semibold">{title}</span>
      <span className="text-xs text-cosmos-300">{description}</span>
    </button>
  );
}
